import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import { Button, Checkbox, ConfigProvider, Divider, Form, Row, Space, Typography } from 'antd';
import React, { useMemo, useState } from 'react';
import { _T } from '../../../../../base-react/src/Localization';
import { useParams } from 'react-router-dom';
import { apis, helpers, hooks, models } from '@vote4dance/judging-sdk';
import { useCompetition } from '../../../../../base-react/hooks/Competition';
import dayjs from 'dayjs';
var Stream = hooks.streams.Stream;
var Api = apis.Api;
var ScheduleSource = models.schedules.ScheduleSource;
var ScheduleRoundSource = models.rounds.ScheduleRoundSource;
var ClassesSource = models.classes.ClassesSource;
var groupRoundListIntoChildren = helpers.groupRoundListIntoChildren;
import { Loading } from '../../../../../base-react/components/Loading';
import { PrintStartNumberHeader } from '../../../competition/teams/components/PrintStartNumber';
import { getFormattedTime, useSchedulePages } from '../../../../../base-react/hooks/Schedule';
import { ScheduleItem } from '../../../../../base-react/models/schedule';
import { RoundItem } from '../../../../../base-react/models/rounds';
import { RoundPathParent, findValidRound } from '../../../../components/RoundPathParent';
const { Title } = Typography;
export const PrintSchedule = () => {
    const { competition_id } = useParams();
    const [schedule, loadingSchedule] = Stream(ScheduleSource(competition_id));
    const [inputCompetition, event, competitions, loadingCompetition] = useCompetition(competition_id);
    const { pages, pageData } = useSchedulePages(competition_id, schedule);
    const [settings, setSettings] = useState({
        includeDetails: false,
        groupByDivisionDiscipline: true
    });
    const [classLookup, setClassLookup] = useState(new Map());
    const sectionData = useMemo(() => {
        const sections = pages.map((page, index) => {
            var _a;
            const items = ((_a = pageData[index]) !== null && _a !== void 0 ? _a : []).filter((item) => item.schedule_id && item.schedule_id !== 'null');
            return {
                page,
                items
            };
        }).filter(({ items }) => items.length > 0);
        const dateSet = new Set(sections
            .map(section => section.page.schedule_date)
            .filter((value) => Boolean(value)));
        const hasMultipleDays = dateSet.size > 1;
        const singleDate = dateSet.size === 1 ? Array.from(dateSet)[0] : undefined;
        return {
            sections,
            hasMultipleDays,
            singleDate
        };
    }, [pages, pageData]);
    const competitionIds = useMemo(() => {
        const ids = new Set();
        if (event === null || event === void 0 ? void 0 : event.competition_id) {
            ids.add(event.competition_id);
        }
        (competitions !== null && competitions !== void 0 ? competitions : []).forEach((competition) => {
            if (competition === null || competition === void 0 ? void 0 : competition.competition_id) {
                ids.add(competition.competition_id);
            }
        });
        if (inputCompetition === null || inputCompetition === void 0 ? void 0 : inputCompetition.competition_id) {
            ids.add(inputCompetition.competition_id);
        }
        return Array.from(ids);
    }, [event, competitions, inputCompetition]);
    const competitionIdsKey = useMemo(() => {
        return competitionIds.slice().sort().join(',');
    }, [competitionIds]);
    React.useEffect(() => {
        let active = true;
        if (!competitionIds.length) {
            setClassLookup(new Map());
            return;
        }
        const loadClasses = async () => {
            try {
                const results = await Promise.all(competitionIds.map((id) => Api.read(ClassesSource(id).url)));
                const nextLookup = new Map();
                results.forEach((classes) => {
                    if (Array.isArray(classes)) {
                        classes.forEach((cls) => {
                            if (cls === null || cls === void 0 ? void 0 : cls.class_id) {
                                const classKey = String(cls.class_id);
                                if (!nextLookup.has(classKey)) {
                                    nextLookup.set(classKey, cls);
                                }
                            }
                        });
                    }
                });
                if (active) {
                    setClassLookup(nextLookup);
                }
            }
            catch (error) {
            }
        };
        loadClasses();
        return () => {
            active = false;
        };
    }, [competitionIdsKey]);
    const downloadCsv = async () => {
        var _a, _b, _c, _d, _e;
        const rows = [
            [
                _T('schedule.column.start'),
                _T('schedule.column.round'),
                _T('schedule.column.beat'),
                _T('schedule.column.floor'),
                _T('schedule.column.heats')
            ]
        ];
        for (let index = 0; index < sectionData.sections.length; index += 1) {
            const section = sectionData.sections[index];
            if (index > 0 && section.page.schedule_type === 'break') {
                rows.push(['', '', '', '', '']);
            }
            const pageLabel = (_a = section.page.schedule_value) !== null && _a !== void 0 ? _a : '';
            if (pageLabel) {
                rows.push(['', pageLabel, '', '', '']);
            }
            for (const item of section.items) {
                const itemTime = (_b = getFormattedTime(item.schedule_start_time)) !== null && _b !== void 0 ? _b : '';
                const itemLabel = (_c = item.schedule_value) !== null && _c !== void 0 ? _c : '';
                let rounds = [];
                if (ScheduleItem.hasRounds(item) && item.schedule_id) {
                    try {
                        const result = await Api.read(ScheduleRoundSource(competition_id, item.schedule_id).url);
                        if (Array.isArray(result)) {
                            rounds = result;
                        }
                    }
                    catch (error) {
                        // fall through with empty rounds
                    }
                }
                rows.push([itemTime, itemLabel, '', '', '']);
                if (!rounds.length) {
                    continue;
                }
                rounds.forEach((round) => {
                    var _a, _b, _c;
                    const roundLabel = [RoundItem.getClassLabel(round), RoundItem.getRoundLabel(round)]
                        .filter(Boolean)
                        .join(' — ');
                    const roundTime = (_a = getFormattedTime(round.round_estimated_time)) !== null && _a !== void 0 ? _a : '';
                    const beat = (_b = round.round_beat) !== null && _b !== void 0 ? _b : '';
                    const floor = round.floor_code || round.floor_name || '';
                    const heats = (_c = formatHeatLabel([round.round_num_heats], true)) !== null && _c !== void 0 ? _c : '';
                    rows.push([roundTime, roundLabel, String(beat), String(floor), heats]);
                });
            }
        }
        const csv = rows.map(buildCsvLine).join('\n');
        const baseName = `${(_d = event === null || event === void 0 ? void 0 : event.competition_date) !== null && _d !== void 0 ? _d : 'schedule'}_${(_e = event === null || event === void 0 ? void 0 : event.competition_name) !== null && _e !== void 0 ? _e : 'competition'}`
            .replace(/\s+/g, '_')
            .replace(/[^A-Za-z0-9_-]/g, '');
        const fileName = `${baseName}_schedule.csv`;
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };
    if (loadingSchedule || loadingCompetition) {
        return React.createElement(Loading, null);
    }
    if (!event) {
        return null;
    }
    document.title = `${event === null || event === void 0 ? void 0 : event.competition_date}_${event === null || event === void 0 ? void 0 : event.competition_name}_schedule`;
    return (React.createElement(ConfigProvider, null,
        React.createElement("style", { type: "text/css", media: "print" }, ` @page { size: portrait; margin: 0 !important } `),
        React.createElement(PrintScheduleSettings, { settings: settings, setSettings: setSettings, onDownloadCsv: downloadCsv }),
        React.createElement("div", { style: { margin: 24, color: '#111' } },
            React.createElement(PrintStartNumberHeader, { event: event, competition: inputCompetition, style: { height: 'inherit' } }),
            sectionData.singleDate && (React.createElement(Typography.Text, { style: { color: '#555' } }, dayjs(sectionData.singleDate, dateFormat).format('LL'))),
            React.createElement("div", { style: { marginTop: 16 } }, sectionData.sections.map((section, index) => {
                var _a;
                return (React.createElement(PrintScheduleSection, { key: (_a = section.page.schedule_id) !== null && _a !== void 0 ? _a : `section-${index}`, event_id: competition_id, page: section.page, items: section.items, showDate: sectionData.hasMultipleDays, includeDetails: settings.includeDetails, groupByDivisionDiscipline: settings.groupByDivisionDiscipline, classLookup: classLookup, isFirst: index === 0 }));
            })))));
};
const PrintScheduleSettings = (props) => {
    const { settings, setSettings, onDownloadCsv } = props;
    const onValuesChange = (values) => {
        setSettings({
            ...settings,
            ...values
        });
    };
    const onFinish = () => {
        window.print();
    };
    return (React.createElement(Row, { style: { width: '100%', paddingLeft: 30, backgroundColor: 'lightgrey' } },
        React.createElement("div", { className: "settings" },
            React.createElement("style", null, `@media print {.settings{display: none;}}`),
            React.createElement(Title, null, _T('common.print', { name: _T('schedule.title').toLowerCase() })),
            React.createElement(Form, { name: "printsettings", initialValues: settings, onValuesChange: onValuesChange, onFinish: onFinish, autoComplete: "off" },
                React.createElement(Form.Item, { name: "groupByDivisionDiscipline", valuePropName: "checked", style: { marginBottom: 8 } },
                    React.createElement(Checkbox, null, "Group by classes")),
                React.createElement(Form.Item, { name: "includeDetails", valuePropName: "checked", style: { marginBottom: 8 } },
                    React.createElement(Checkbox, null, "Include details")),
                React.createElement(Form.Item, { style: { marginBottom: 8 } },
                    React.createElement(Space, null,
                        React.createElement(Button, { icon: React.createElement(PrinterOutlined, null), type: "primary", htmlType: "submit" }, _T('common.print')),
                        React.createElement(Button, { icon: React.createElement(DownloadOutlined, null), onClick: onDownloadCsv }, _T('data.export.csv'))))))));
};
const dateFormat = 'YYYY-MM-DD';
const PrintScheduleSection = (props) => {
    const { event_id, page, items, showDate, includeDetails, groupByDivisionDiscipline, classLookup, isFirst } = props;
    const dateLabel = page.schedule_date
        ? dayjs(page.schedule_date, dateFormat).format('LL')
        : null;
    const shouldBreak = page.schedule_type === 'break';
    const sectionStyle = {
        marginBottom: 24,
        breakBefore: 'auto',
        pageBreakBefore: 'auto',
        breakAfter: shouldBreak ? 'page' : 'auto',
        pageBreakAfter: shouldBreak ? 'always' : 'auto'
    };
    return (React.createElement("div", { style: sectionStyle },
        React.createElement(Divider, null),
        React.createElement(Space, { orientation: "vertical", size: 2 },
            React.createElement(Title, { level: 3, style: { marginBottom: 0 } }, page.schedule_value),
            showDate && dateLabel && (React.createElement(Typography.Text, { style: { color: '#555' } }, dateLabel))),
        React.createElement("div", { style: { marginTop: 12 } }, items.map((item, index) => {
            var _a;
            return (React.createElement(PrintScheduleItem, { key: (_a = item.schedule_id) !== null && _a !== void 0 ? _a : `item-${index}`, event_id: event_id, item: item, includeDetails: includeDetails, groupByDivisionDiscipline: groupByDivisionDiscipline, classLookup: classLookup }));
        }))));
};
const PrintScheduleItem = (props) => {
    const { event_id, item, includeDetails, groupByDivisionDiscipline, classLookup } = props;
    const hasRounds = ScheduleItem.hasRounds(item);
    const timeLabel = getFormattedTime(item.schedule_start_time);
    const valueStyle = item.schedule_type === 'comment'
        ? { fontStyle: 'italic', color: '#444' }
        : { fontWeight: 600 };
    return (React.createElement("div", { style: { marginBottom: 12, breakInside: 'avoid', pageBreakInside: 'avoid' } },
        React.createElement("div", { style: { display: 'flex', gap: 12, alignItems: 'baseline' } },
            React.createElement("div", { style: { width: 72, fontVariantNumeric: 'tabular-nums' } }, timeLabel),
            React.createElement("div", { style: { flex: 1 } },
                React.createElement("span", { style: valueStyle }, item.schedule_value))),
        hasRounds && item.schedule_id && (React.createElement("div", { style: { marginLeft: 84, marginTop: 6 } },
            React.createElement(ScheduleRoundList, { event_id: event_id, schedule_id: item.schedule_id, includeDetails: includeDetails, groupByDivisionDiscipline: groupByDivisionDiscipline, classLookup: classLookup })))));
};
const ScheduleRoundList = (props) => {
    const { event_id, schedule_id, includeDetails, groupByDivisionDiscipline, classLookup } = props;
    const [rounds, loadingRounds] = Stream(ScheduleRoundSource(event_id, schedule_id));
    const groupedRounds = useMemo(() => { var _a; return (_a = groupRoundListIntoChildren((rounds !== null && rounds !== void 0 ? rounds : []))) !== null && _a !== void 0 ? _a : []; }, [rounds]);
    const showRoundTimes = useMemo(() => (rounds !== null && rounds !== void 0 ? rounds : []).some((round) => !!round.round_estimated_time), [rounds]);
    const getRepresentative = (round) => { var _a; return round.isPlaceholder && round.children ? ((_a = findValidRound(round.children)) !== null && _a !== void 0 ? _a : round) : round; };
    const getGroupKey = (round) => {
        var _a, _b, _c, _d;
        if (!groupByDivisionDiscipline) {
            return null;
        }
        const classData = round.class_id ? classLookup.get(String(round.class_id)) : undefined;
        const divisionId = (_b = (_a = classData === null || classData === void 0 ? void 0 : classData.division_id) !== null && _a !== void 0 ? _a : round.division_id) !== null && _b !== void 0 ? _b : null;
        const disciplineId = (_d = (_c = classData === null || classData === void 0 ? void 0 : classData.discipline_id) !== null && _c !== void 0 ? _c : round.discipline_id) !== null && _d !== void 0 ? _d : null;
        if (divisionId != null && disciplineId != null) {
            return `${divisionId}:${disciplineId}`;
        }
        const classLabel = RoundItem.getClassLabel(round);
        if (!classLabel) {
            return null;
        }
        return normalizeGroupLabel(classLabel);
    };
    const displayEntries = useMemo(() => {
        const entries = [];
        groupedRounds.forEach((round, index) => {
            var _a, _b;
            const representative = getRepresentative(round);
            const groupKey = getGroupKey(representative);
            const last = entries[entries.length - 1];
            if (groupKey && last && last.groupKey === groupKey) {
                last.rounds.push(representative);
            }
            else {
                entries.push({
                    key: (_b = (_a = representative.round_id) !== null && _a !== void 0 ? _a : representative.class_id) !== null && _b !== void 0 ? _b : `${representative.round_label}-${index}`,
                    rounds: [representative],
                    representative,
                    groupKey
                });
            }
        });
        return entries;
    }, [groupedRounds, groupByDivisionDiscipline, classLookup]);
    if (loadingRounds) {
        return React.createElement(Loading, null);
    }
    if (!groupedRounds.length) {
        return null;
    }
    const renderRound = (entry) => {
        var _a, _b;
        const { rounds, representative, key, groupKey } = entry;
        const classData = representative.class_id ? classLookup.get(String(representative.class_id)) : undefined;
        const divisionName = (_a = representative.division_name) !== null && _a !== void 0 ? _a : classData === null || classData === void 0 ? void 0 : classData.division_name;
        const disciplineName = (_b = representative.discipline_name) !== null && _b !== void 0 ? _b : classData === null || classData === void 0 ? void 0 : classData.discipline_name;
        const groupLabel = [divisionName, disciplineName].filter(Boolean).join(' — ');
        const classLabels = Array.from(new Set(rounds.map((round) => RoundItem.getClassLabel(round)).filter(Boolean)));
        const mergedClassLabel = mergeClassLabels(classLabels);
        const roundLabels = Array.from(new Set(rounds.map((round) => RoundItem.getRoundLabel(round)).filter(Boolean)));
        const shouldSplitRows = groupKey && rounds.length > 1 && roundLabels.length > 1;
        const fallbackLabel = groupLabel || RoundItem.getClassLabel(representative);
        const floorValue = representative.floor_code || representative.floor_name;
        const beatValue = representative.round_beat;
        let prefixParts = '';
        if (beatValue && floorValue) {
            prefixParts = representative.floor_code ? `${beatValue}${floorValue}` : `${beatValue} ${floorValue}`;
        }
        else if (beatValue) {
            prefixParts = `${beatValue}`;
        }
        else if (floorValue) {
            prefixParts = `${floorValue}`;
        }
        const prefixLabel = prefixParts ? `${prefixParts} ` : '';
        const baseLabel = mergedClassLabel || fallbackLabel;
        const isGrouped = Boolean(groupKey && rounds.length > 1);
        const displayPrefixLabel = groupByDivisionDiscipline ? '' : prefixLabel;
        const labelRows = shouldSplitRows
            ? roundLabels.map((roundLabel) => {
                var _a;
                const matchingRounds = rounds.filter((round) => RoundItem.getRoundLabel(round) === roundLabel);
                const rowClassLabels = Array.from(new Set(matchingRounds.map((round) => RoundItem.getClassLabel(round)).filter(Boolean)));
                const rowUsesFullSet = rowClassLabels.length === classLabels.length && classLabels.length > 0;
                const rowBaseLabel = rowUsesFullSet
                    ? baseLabel
                    : (mergeClassLabels(rowClassLabels) || fallbackLabel);
                return {
                    label: [rowBaseLabel, roundLabel].filter(Boolean).join(' — '),
                    round: (_a = matchingRounds[0]) !== null && _a !== void 0 ? _a : representative,
                    rounds: matchingRounds
                };
            })
            : [{
                    label: groupKey && rounds.length > 1
                        ? [baseLabel, roundLabels.join(', ')].filter(Boolean).join(' — ')
                        : null,
                    round: representative,
                    rounds
                }];
        return (React.createElement("div", { key: key, style: { marginBottom: 4 } }, labelRows.map((row, rowIndex) => {
            var _a;
            const timeValue = row.round.round_estimated_time;
            const showTimeColumn = showRoundTimes && !groupByDivisionDiscipline;
            const timeLabel = showTimeColumn ? getFormattedTime(timeValue) : '';
            const heatsLabel = includeDetails
                ? formatHeatLabel(((_a = row.rounds) !== null && _a !== void 0 ? _a : [row.round]).map((item) => item.round_num_heats))
                : null;
            return (React.createElement("div", { key: `${key}-${rowIndex}`, style: { display: 'flex', gap: 12, alignItems: 'baseline', marginBottom: rowIndex < labelRows.length - 1 ? 4 : 0 } },
                showTimeColumn && (React.createElement("div", { style: { width: 52, textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }, timeLabel)),
                React.createElement("div", { style: { fontWeight: 600 } },
                    row.label ? (`${displayPrefixLabel}${row.label}`) : (React.createElement(RoundPathParent, { record: representative }, (round, parents) => {
                        var _a;
                        const classLabel = RoundItem.getClassLabel(round);
                        const parentLabels = (_a = parents === null || parents === void 0 ? void 0 : parents.map((parent) => RoundItem.getRoundLabel(parent)).filter(Boolean)) !== null && _a !== void 0 ? _a : [];
                        const roundLabel = RoundItem.getRoundLabel(round);
                        const parts = parents
                            ? [classLabel, ...parentLabels].filter(Boolean)
                            : [classLabel, roundLabel].filter(Boolean);
                        return `${displayPrefixLabel}${parts.join(' — ')}`;
                    })),
                    heatsLabel && React.createElement("span", { style: { fontWeight: 400, color: '#555' } }, ` (${heatsLabel})`))));
        })));
    };
    return (React.createElement("div", null, displayEntries.map((entry) => renderRound(entry))));
};
const mergeClassLabels = (labels) => {
    var _a;
    const uniqueLabels = Array.from(new Set(labels.filter((label) => Boolean(label))));
    if (uniqueLabels.length <= 1) {
        return (_a = uniqueLabels[0]) !== null && _a !== void 0 ? _a : '';
    }
    const tokenized = uniqueLabels.map((label) => label.trim().split(/\s+/));
    const minTokens = Math.min(...tokenized.map((tokens) => tokens.length));
    let prefixLength = 0;
    for (let i = 0; i < minTokens; i += 1) {
        const token = tokenized[0][i];
        if (tokenized.every((tokens) => tokens[i] === token)) {
            prefixLength += 1;
        }
        else {
            break;
        }
    }
    if (prefixLength === 0) {
        return uniqueLabels.join(' / ');
    }
    const base = tokenized[0].slice(0, prefixLength).join(' ');
    const suffixes = uniqueLabels.map((label, index) => {
        const suffix = tokenized[index].slice(prefixLength).join(' ').trim();
        return suffix || label;
    });
    const uniqueSuffixes = Array.from(new Set(suffixes));
    return `${base} ${uniqueSuffixes.join(' / ')}`.trim();
};
const normalizeGroupLabel = (label) => {
    const tokens = label.trim().split(/\s+/);
    if (tokens.length > 1) {
        const lastToken = tokens[tokens.length - 1];
        if (/^[A-Za-z]\d?$/.test(lastToken)) {
            return tokens.slice(0, -1).join(' ').toLowerCase();
        }
    }
    return label.trim().toLowerCase();
};
const formatHeatLabel = (values, omitPrefix) => {
    const heats = values
        .map((value) => (value == null ? null : Number(value)))
        .filter((value) => value != null && !Number.isNaN(value));
    if (!heats.length) {
        return null;
    }
    const [first, ...rest] = heats;
    const base = omitPrefix ? String(first) : _T('round.heat', { number: first });
    if (!rest.length || rest.every((value) => value === first)) {
        return base;
    }
    return `${base} / ${rest.join(' / ')}`;
};
const buildCsvLine = (cells) => {
    return cells.map(csvEscape).join(',');
};
const csvEscape = (value) => {
    if (value == null) {
        return '';
    }
    const stringValue = String(value);
    if (/[",\n]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
};
